import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { FirebaseService } from '../firebase/firebase.service';
import { UserService } from '../user/user.service';
import { LoggingChannels, LoggingService } from '../logging/logging.service';

export type ThemeMode = 'light' | 'dark' | 'system';

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`;
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch {}
  return null;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public mode: ThemeMode = 'system';

  private systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');

  constructor(
    private localStorage: LocalStorageService,
    private firebase: FirebaseService,
    private user: UserService,
    private logging: LoggingService,
  ) {
    this.initialize();
    // The theme saved on the user document wins over this device's own copy, so a change on another device
    // (the web, the phone) arrives here. Optional chaining: some specs provide a bare UserService.
    this.user.preferences$?.subscribe(({ theme }) => this.applyRemote(theme));
  }

  public initialize(): void {
    const stored = this.localStorage.getItem('theme') as ThemeMode;
    const cookie = getCrewdleCookie('theme') as ThemeMode;

    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      this.mode = stored;
    } else if (cookie && ['light', 'dark', 'system'].includes(cookie)) {
      this.mode = cookie;
      this.localStorage.setItem('theme', cookie);
    }

    this.applyTheme();

    this.systemDarkQuery.addEventListener('change', () => {
      if (this.mode === 'system') {
        this.applyTheme();
      }
    });

    // Sync theme from cookie when returning to this tab (cross-platform sync)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const cookieTheme = getCrewdleCookie('theme') as ThemeMode | null;
      if (cookieTheme && ['light', 'dark', 'system'].includes(cookieTheme) && cookieTheme !== this.mode) {
        this.mode = cookieTheme;
        this.localStorage.setItem('theme', cookieTheme);
        this.applyTheme();
      }
    });
  }

  public setMode(mode: ThemeMode): void {
    this.mode = mode;
    this.localStorage.setItem('theme', mode);
    setCrewdleCookie('theme', mode);
    this.applyTheme();
    this.syncToFirestore();
  }

  /** The theme read from the user document: applied and remembered here, never written back (it came from there). */
  private applyRemote(theme: string | undefined): void {
    if (!theme || !['light', 'dark', 'system'].includes(theme) || theme === this.mode) return;
    this.mode = theme as ThemeMode;
    this.localStorage.setItem('theme', theme);
    setCrewdleCookie('theme', theme);
    this.applyTheme();
  }

  public async loadUserTheme(): Promise<void> {
    try {
      const uid = this.user.user?.uid;
      if (!uid) {
        return;
      }
      const localTheme = this.localStorage.getItem('theme');
      const doc = await this.firebase.globalFirestore.docGet(`users/${uid}`);
      const firestoreTheme = doc.exists() ? (doc.data() as any)?.theme : null;

      if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
        // Local preference exists — push it to Firestore if different
        if (firestoreTheme !== localTheme) {
          await this.firebase.globalFirestore.docSet(`users/${uid}`, { theme: localTheme }, { merge: true });
        }
        setCrewdleCookie('theme', localTheme);
      } else if (firestoreTheme && ['light', 'dark', 'system'].includes(firestoreTheme)) {
        // No local preference — use Firestore value
        this.mode = firestoreTheme;
        this.localStorage.setItem('theme', firestoreTheme);
        setCrewdleCookie('theme', firestoreTheme);
        this.applyTheme();
      }
    } catch (err) {
      this.logging.log('ThemeService: error loading user theme', LoggingChannels.Default, err);
    }
  }

  private applyTheme(): void {
    const isDark = this.mode === 'dark' || (this.mode === 'system' && this.systemDarkQuery.matches);
    document.body.classList.toggle('dark', isDark);
  }

  private async syncToFirestore(): Promise<void> {
    try {
      const uid = this.user.user?.uid;
      if (!uid) {
        return;
      }
      await this.firebase.globalFirestore.docSet(`users/${uid}`, {
        theme: this.mode,
      }, { merge: true });
    } catch (err) {
      this.logging.log('ThemeService: error syncing theme to Firestore', LoggingChannels.Default, err);
    }
  }
}
